import "@/index.css";
import "@/portfolio.css";
import "@/admin.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n";
import { isTracked, sendView, sendDuration } from "@/lib/analytics";
import Home from "@/pages/Home";
import MapPage from "@/pages/Map";
import Ask from "@/pages/Ask";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import Analytics from "@/pages/Analytics";
import PostEditor from "@/pages/PostEditor";
import PreviewPage from "@/pages/PreviewPage";
import AuthCallback from "@/pages/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";

function Tracker() {
  const location = useLocation();
  const current = useRef({ path: null, start: 0 });

  useEffect(() => {
    const path = location.pathname;
    if (isTracked(path)) {
      sendView(path);
      current.current = { path, start: Date.now() };
    } else {
      current.current = { path: null, start: 0 };
    }
    return () => {
      const c = current.current;
      if (c.path && c.start) sendDuration(c.path, Date.now() - c.start);
    };
  }, [location.pathname]);

  useEffect(() => {
    const flush = () => {
      const c = current.current;
      if (c.path && c.start) {
        sendDuration(c.path, Date.now() - c.start);
        current.current = { ...c, start: Date.now() };
      }
    };
    const onVis = () => { if (document.hidden) flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return null;
}

function SecurityGuard() {
  useEffect(() => {
    const allowIn = (t) =>
      t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
    const onCtx = (e) => { if (!allowIn(e.target)) e.preventDefault(); };
    const onKey = (e) => {
      const k = (e.key || "").toUpperCase();
      if (k === "F12") { e.preventDefault(); return; }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ["I", "J", "C"].includes(k)) { e.preventDefault(); return; }
      if ((e.ctrlKey || e.metaKey) && k === "U" && !allowIn(e.target)) { e.preventDefault(); }
    };
    const onDrag = (e) => { if (e.target?.tagName === "IMG") e.preventDefault(); };
    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("keydown", onKey);
    document.addEventListener("dragstart", onDrag);
    return () => {
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("dragstart", onDrag);
    };
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <SecurityGuard />
      <Tracker />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/ask" element={<Ask />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/admin/posts/new" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
        <Route path="/admin/posts/:id/edit" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
        <Route path="/preview/:id" element={<ProtectedRoute><PreviewPage /></ProtectedRoute>} />
        <Route path="/:slug" element={<Home />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
